import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  getDay,
  addDays,
  subDays,
} from 'date-fns'
import type { InquiryLite, EventLite } from '@/gen/cortado/v1/inquiries_pb'
import type { TaskLite } from '@/gen/cortado/v1/actions_pb'
import { EventStageEnum, InquiryStageEnum, TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import type { CalendarEvent } from '@/components/calendar/calendar-context'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampToDateOnly } from '@/lib/date-utils'

/**
 * Category colors for the three calendar types (Leads/Events/Tasks). These
 * feed non-Tailwind contexts (inline `style.backgroundColor`), so they
 * reference the theme's chart tokens directly via `var()` rather than
 * duplicating hex values — stays harmonized and in sync with `styles.css`
 * automatically. Shared by calendar-context.tsx (calendar toggle rows) and
 * anywhere else that needs the raw category color.
 */
export const CALENDAR_CATEGORY_COLORS = {
  leads: 'var(--chart-2)',
  events: 'var(--chart-3)',
  tasks: 'var(--dim)',
} as const

export type CalendarView = 'month' | 'week' | 'day'

export interface DateRange {
  start: Date
  end: Date
}

/**
 * Calculate date range for calendar views
 * Month view: includes overflow days for grid completeness
 * Week view: Sunday to Saturday
 * Day view: single day
 */
export function getDateRangeForView(currentDate: Date, view: CalendarView): DateRange {
  switch (view) {
    case 'month': {
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(currentDate)

      // Include overflow days to fill calendar grid (always starts Sunday)
      const calendarStart = subDays(monthStart, getDay(monthStart))
      const calendarEnd = addDays(monthEnd, 6 - getDay(monthEnd))

      return { start: calendarStart, end: calendarEnd }
    }

    case 'week': {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 0 }), // Sunday
        end: endOfWeek(currentDate, { weekStartsOn: 0 }), // Saturday
      }
    }

    case 'day': {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate),
      }
    }
  }
}

/**
 * Convert protobuf Timestamp to JavaScript Date
 */
export function timestampToDate(timestamp: Timestamp): Date {
  return new Date(Number(timestamp.seconds) * 1000)
}

/**
 * Format enum name to human-readable string
 */
function formatEnumName(enumValue: string): string {
  return enumValue
    .split('_')
    .slice(1) // Remove prefix (e.g., EVENT_TYPE_)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Map InquiryLite to CalendarEvents using inquiry_events for multi-day support.
 * Returns one CalendarEvent per inquiry_event date.
 *
 * @param getEventTypeLabel - Resolves an organization event type key to its
 *   display label (from `useEventTypes()`); event types are per-org data, so
 *   this module can't derive the label on its own.
 */
export function mapInquiryToCalendarEvents(
  inquiry: InquiryLite,
  getEventTypeLabel: (key: string | undefined) => string
): CalendarEvent[] {
  const contactName = `${inquiry.contactFirstName} ${inquiry.contactLastName}`.trim()
  const inquiryEvents = inquiry.inquiryEvents ?? []

  // Use inquiry_events if available for multi-day support
  if (inquiryEvents.length > 0) {
    return inquiryEvents
      .filter(ie => !!ie.eventDate)
      .map(ie => {
        const startDate = timestampToDate(ie.eventDate!)
        const endDate = ie.eventLength
          ? new Date(startDate.getTime() + ie.eventLength * 60 * 60 * 1000)
          : new Date(startDate.getTime() + 60 * 60 * 1000)

        return {
          id: `${inquiry.id}-${ie.id}`,
          title: contactName || 'Unnamed Lead',
          description: getEventTypeLabel(ie.eventTypeKey || inquiry.eventTypeKey) || undefined,
          startDate,
          endDate,
          color: CALENDAR_CATEGORY_COLORS.leads,
          category: 'Lead',
          isAllDay: false,
          location: ie.eventLocation,
          calendarId: 'leads',
          organizationId: inquiry.organizationId,
          eventType: ie.eventTypeKey ?? inquiry.eventTypeKey,
          eventGuests: ie.eventGuests,
          eventLength: ie.eventLength,
          eventTimezone: ie.eventTimezone,
          createdAt: inquiry.createdAt ? timestampToDate(inquiry.createdAt) : undefined,
          stage: inquiry.stage,
          inquiryId: inquiry.id,
        } satisfies CalendarEvent
      })
  }

  // Fallback to firstEventDate if no inquiry_events
  if (!inquiry.firstEventDate) return []

  const startDate = timestampToDate(inquiry.firstEventDate)
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000)

  return [{
    id: inquiry.id,
    title: contactName || 'Unnamed Lead',
    description: getEventTypeLabel(inquiry.eventTypeKey) || undefined,
    startDate,
    endDate,
    color: CALENDAR_CATEGORY_COLORS.leads,
    category: 'Lead',
    isAllDay: false,
    location: undefined,
    calendarId: 'leads',
    organizationId: inquiry.organizationId,
    eventType: inquiry.eventTypeKey,
    eventGuests: undefined,
    eventLength: undefined,
    eventTimezone: inquiry.firstEventTimezone,
    createdAt: inquiry.createdAt ? timestampToDate(inquiry.createdAt) : undefined,
    stage: inquiry.stage,
  }]
}

/**
 * Map EventLite to CalendarEvent
 * Used for "Events" calendar type
 */
export function mapEventToCalendarEvent(event: EventLite): CalendarEvent | null {
  // Skip events without event_date
  if (!event.eventDate) return null

  const startDate = timestampToDate(event.eventDate)
  const endDate = event.eventLength
    ? new Date(startDate.getTime() + event.eventLength * 60 * 60 * 1000)
    : new Date(startDate.getTime() + 60 * 60 * 1000) // Default 1 hour

  const contactName = `${event.contactFirstName} ${event.contactLastName}`.trim()

  return {
    id: event.id,
    title: contactName || 'Unnamed Event',
    description: event.stage ? formatEnumName(EventStageEnum[event.stage]) : undefined,
    startDate,
    endDate,
    color: CALENDAR_CATEGORY_COLORS.events,
    category: 'Event',
    isAllDay: false,
    location: undefined, // EventLite doesn't expose location in proto
    calendarId: 'events',
    organizationId: event.organizationId,
    eventGuests: event.eventGuests,
    eventLength: event.eventLength,
    eventTimezone: event.eventTimezone,
    createdAt: event.createdAt ? timestampToDate(event.createdAt) : undefined,
    stage: event.stage,
  }
}

/**
 * Notion-style CSS classes for calendar event cards, mapped by stage.
 * Uses pastel backgrounds with colored left borders and matching dark text.
 */
// Kept in sync with calendar-legend.tsx's LEAD_ENTRIES/EVENT_ENTRIES/TASK_ENTRIES swatches.
const LEAD_STAGE_STYLES: Record<number, string> = {
  [InquiryStageEnum.INQUIRY_STAGE_NEW]: 'bg-status-info-bg border border-dashed border-status-info-border text-status-info-fg',
  [InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS]: 'bg-status-warning-bg border border-dashed border-status-warning-border text-status-warning-fg',
  [InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT]: 'bg-purple-50 border border-dashed border-purple-300 text-purple-700 dark:bg-purple-950 dark:border-purple-800 dark:text-purple-300',
  [InquiryStageEnum.INQUIRY_STAGE_ACCEPTED]: 'bg-status-success-bg border border-dashed border-status-success-border text-status-success-fg',
  [InquiryStageEnum.INQUIRY_STAGE_REJECTED]: 'bg-status-danger-bg border border-dashed border-status-danger-border text-status-danger-fg line-through',
  [InquiryStageEnum.INQUIRY_STAGE_STALE]: 'bg-status-neutral-bg border border-dashed border-status-neutral-border text-status-neutral-fg',
  [InquiryStageEnum.INQUIRY_STAGE_REFERRED]: 'bg-orange-50 border border-dashed border-orange-300 text-orange-700 dark:bg-orange-950 dark:border-orange-800 dark:text-orange-300',
}

const EVENT_STAGE_STYLES: Record<number, string> = {
  [EventStageEnum.EVENT_STAGE_UPCOMING]: 'bg-status-info-bg border-l-4 border-status-info-fg text-status-info-fg',
  [EventStageEnum.EVENT_STAGE_COMPLETE]: 'bg-status-success-bg border-l-4 border-status-success-fg text-status-success-fg',
  [EventStageEnum.EVENT_STAGE_CANCELLED]: 'bg-status-danger-bg border-l-4 border-status-danger-fg text-status-danger-fg line-through',
  [EventStageEnum.EVENT_STAGE_RESCHEDULED]: 'bg-status-warning-bg border-l-4 border-status-warning-fg text-status-warning-fg',
  [EventStageEnum.EVENT_STAGE_POSTPONED]: 'bg-orange-50 border-l-4 border-orange-500 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
}

const TASK_STATUS_STYLES: Record<number, string> = {
  [TaskStatusEnum.TASK_STATUS_NOT_STARTED]: 'bg-secondary border-l-4 border-border text-secondary-foreground',
  [TaskStatusEnum.TASK_STATUS_IN_PROGRESS]: 'bg-status-warning-bg border-l-4 border-status-warning-fg text-status-warning-fg',
  [TaskStatusEnum.TASK_STATUS_COMPLETED]: 'bg-status-success-bg border-l-4 border-status-success-fg text-status-success-fg line-through',
}

export function getEventStageClassName(calendarId: string, stage?: number): string {
  if (calendarId === 'leads') {
    return LEAD_STAGE_STYLES[stage ?? 0] ?? 'bg-status-success-bg border border-dashed border-status-success-border text-status-success-fg'
  }
  if (calendarId === 'events') {
    return EVENT_STAGE_STYLES[stage ?? 0] ?? 'bg-status-info-bg border-l-4 border-status-info-fg text-status-info-fg'
  }
  if (calendarId === 'tasks') {
    return TASK_STATUS_STYLES[stage ?? 0] ?? 'bg-secondary border-l-4 border-border text-secondary-foreground'
  }
  return 'bg-muted border border-border text-muted-foreground'
}

/**
 * Map TaskLite to CalendarEvent using due date
 */
export function mapTaskToCalendarEvent(task: TaskLite): CalendarEvent | null {
  if (!task.dueDate) return null

  // Due dates are date-only values stored in a Postgres `date` column; read the
  // UTC calendar day so the task lands on the day the user picked rather than
  // the prior day in behind-UTC zones.
  const startDate = timestampToDateOnly(task.dueDate)
  if (!startDate) return null
  // Tasks are all-day items pinned to the due date
  const endDate = new Date(startDate)
  endDate.setHours(23, 59, 59, 999)

  const assigneeInitials = getInitials(task.assignedToFirstName, task.assignedToLastName)

  return {
    id: task.id,
    title: task.title,
    description: task.parentName ?? undefined,
    startDate,
    endDate,
    color: CALENDAR_CATEGORY_COLORS.tasks,
    category: 'Task',
    isAllDay: true,
    calendarId: 'tasks',
    stage: task.taskStatus,
    createdAt: task.createdAt ? timestampToDate(task.createdAt) : undefined,
    // Task-specific fields
    taskStatus: task.taskStatus,
    assigneeInitials,
    assignedToFirstName: task.assignedToFirstName,
    assignedToLastName: task.assignedToLastName,
    parentType: task.parentType,
    parentId: task.parentId,
    parentName: task.parentName,
  }
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() ?? ''
  const last = lastName?.charAt(0)?.toUpperCase() ?? ''
  return first + last || '?'
}
